import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gcalStatus, gcalConnect, gcalDisconnect, hrmsNudgeAttendance, slackStatus, slackTest, hrmsSendJoinerDigest } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { CheckCircle2, XCircle, Link2, Unlink, Bell, Loader2, Calendar, Slack, Send, Mail } from 'lucide-react';

const SettingsPage = () => {
  const { toast } = useToast();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [sl, setSl] = useState({ finance_configured: false, recruitment_configured: false });
  const [busy, setBusy] = useState(false);
  const [nudging, setNudging] = useState(false);

  const load = async () => {
    setStatus(await gcalStatus().catch(() => null));
    setSl(await slackStatus().catch(() => ({ finance_configured: false, recruitment_configured: false })));
  };

  useEffect(() => {
    load();
    const q = params.get('gcal');
    if (q === 'connected') toast({ title: 'Google Calendar connected' });
    if (q === 'error') toast({ title: 'Google connection failed', description: 'Please retry or contact your admin.' });
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try {
      const { authorization_url } = await gcalConnect();
      window.location.href = authorization_url;
    } catch (err) {
      toast({ title: 'Cannot connect', description: err?.response?.data?.detail || '' });
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Future interviews will no longer create Meet links automatically.')) return;
    await gcalDisconnect();
    toast({ title: 'Disconnected' });
    load();
  };
  const runNudge = async () => {
    setNudging(true);
    try {
      const res = await hrmsNudgeAttendance();
      toast({ title: `Nudge sent`, description: `${res.reminders_sent} of ${res.candidates} employees notified for ${res.date}` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '' });
    } finally { setNudging(false); }
  };

  const testSlack = async (kind) => {
    try {
      const res = await slackTest(kind);
      toast({ title: res.ok ? 'Slack ping sent' : 'Slack ping failed', description: kind });
    } catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
  };

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl font-extrabold">Settings &amp; integrations</h1>
      <p className="text-sm text-slate-500 mt-1">Connect your Google Calendar so scheduled interviews create real Google Meet links and land on your calendar automatically.</p>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {/* Google Calendar */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="gcal-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Calendar className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Google Calendar</h2>
                  <p className="text-xs text-slate-500">Auto-create Meet events for interviews you schedule.</p>
                </div>
              </div>
            </div>
            {status?.connected ? <Badge className="bg-emerald-500">Connected</Badge> : <Badge className="bg-slate-500">Not connected</Badge>}
          </div>
          {!status?.configured && (
            <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
              Not configured on this server yet. An admin must set <code className="font-mono">GOOGLE_CLIENT_ID</code>, <code className="font-mono">GOOGLE_CLIENT_SECRET</code> and <code className="font-mono">APP_URL</code> in <code className="font-mono">backend/.env</code>.
            </div>
          )}
          {status?.connected && (
            <div className="mt-4 text-sm text-slate-700">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Signed in as <b>{status?.account_email || 'your account'}</b></div>
              <p className="text-xs text-slate-500 mt-1">New interviews will get a real Meet link and be added to this calendar with invitees notified.</p>
            </div>
          )}
          <div className="mt-5 flex gap-2">
            {!status?.connected && <Button onClick={connect} disabled={busy || !status?.configured} className="bg-blue-600 hover:bg-blue-700" data-testid="gcal-connect">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1" /> Connect Google Calendar</>}
            </Button>}
            {status?.connected && <Button onClick={disconnect} variant="outline" className="border-red-200 text-red-600" data-testid="gcal-disconnect"><Unlink className="h-4 w-4 mr-1" /> Disconnect</Button>}
          </div>
        </div>

        {/* Attendance nudge */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="attendance-nudge-card">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center"><Bell className="h-5 w-5 text-orange-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">End-of-day clock-out nudge</h2>
                <p className="text-xs text-slate-500">Automated at 20:00 IST daily. Manually trigger now:</p>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <Button onClick={runNudge} disabled={nudging} className="bg-orange-500 hover:bg-orange-600" data-testid="attendance-nudge-run">
              {nudging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4 mr-1" /> Send reminders now</>}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-4">Employees with an open clock-in for today will get a friendly nudge email. Reminders are sent at most once per day.</p>
        </div>

        {/* Slack Finance */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="slack-finance-card">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center"><Slack className="h-5 w-5 text-purple-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Slack · Finance</h2>
                <p className="text-xs text-slate-500">Ping when payroll runs complete.</p>
              </div>
            </div>
            <Badge className={sl.finance_configured ? 'bg-emerald-500' : 'bg-slate-500'}>{sl.finance_configured ? 'Connected' : 'Not configured'}</Badge>
          </div>
          {!sl.finance_configured && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">Set <code className="font-mono">SLACK_FINANCE_WEBHOOK</code> in <code className="font-mono">backend/.env</code> with your Slack incoming-webhook URL.</div>}
          <div className="mt-5">
            <Button onClick={() => testSlack('finance')} disabled={!sl.finance_configured} className="bg-purple-600 hover:bg-purple-700" data-testid="slack-test-finance"><Send className="h-4 w-4 mr-1" /> Send test ping</Button>
          </div>
        </div>

        {/* Slack Recruitment */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="slack-recruitment-card">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Slack className="h-5 w-5 text-blue-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Slack · Recruitment</h2>
                <p className="text-xs text-slate-500">Feed of new applications, interviews, offers and joins.</p>
              </div>
            </div>
            <Badge className={sl.recruitment_configured ? 'bg-emerald-500' : 'bg-slate-500'}>{sl.recruitment_configured ? 'Connected' : 'Not configured'}</Badge>
          </div>
          {!sl.recruitment_configured && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">Set <code className="font-mono">SLACK_RECRUITMENT_WEBHOOK</code> in <code className="font-mono">backend/.env</code>.</div>}
          <div className="mt-5">
            <Button onClick={() => testSlack('recruitment')} disabled={!sl.recruitment_configured} className="bg-blue-600 hover:bg-blue-700" data-testid="slack-test-recruitment"><Send className="h-4 w-4 mr-1" /> Send test ping</Button>
          </div>
        </div>

        {/* Weekly digest */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="digest-card">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Mail className="h-5 w-5 text-emerald-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">New-joiner weekly digest</h2>
                <p className="text-xs text-slate-500">Sent to HR automatically every Monday 09:00 IST.</p>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <Button onClick={async () => {
              try { const r = await hrmsSendJoinerDigest(); toast({ title: 'Digest sent', description: `${r.joiners} joiners → ${r.sent_to} HR recipients` }); }
              catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || '' }); }
            }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="digest-send-now"><Send className="h-4 w-4 mr-1" /> Send digest now</Button>
          </div>
          <p className="text-xs text-slate-500 mt-4">Includes progress %, missing documents, buddy assignment and handbook acknowledgement for anyone onboarded in the last 30 days.</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
